import User from "./user";
import ModuleCard from "./module-card";
import Msg from "./message";
import ListView from "./list-view";
import List from "./list";
import Field from "./field";
import ExcelExportTemplate from "./excel-export-template";
import Auth from "./auth";
import Api from "./api";
import { deepDecode, deepEncode, filter, sortBy } from "../utils";
import IRelationPathField from "../interface/relation-path-field";
import IModuleCard from "../interface/module-card";
import IListView from "../interface/list-view";
import IList from "../interface/list";
import IField from "../interface/field";
import IEntityFilterCandidate from "../interface/entity-filter-candidate";
import IEntityField from "../interface/entity-field";
import IEntity from "../interface/entity";
import { IConnectionEntity } from "../interface/data-connection";
import ICardWidget from "../interface/card-widget";
import ICardList from "../interface/card-list";
import IApiParamEntity from "../interface/api/api-param-entity";
import IApiField from "../interface/api/api-field";
import IApiEntity from "../interface/api/api-entity";
import Global from "../global";
import SpecialProc from "../enum/special-proc";
import ParamStorage from "../enum/param-storage";
import Message from "../enum/message";
import { EntityFilterTypes, PrimaryTypes, ServerTypes } from "../enum/field-types";
import FieldType from "../enum/field-type";
import FieldSystemType from "../enum/field-system-type";
import ErrorCode from "../enum/error-code";
import CardType from "../enum/card-type";

class EntitySingleton {

    /* Public Methods */

    public getAll(): IEntity[] {
        return this.getAllRaw().map((e: IApiEntity) => this.toClient(e));
    }

    public getAllRaw(): IApiEntity[] {
        // TODO - Backbone delete
        if (Auth.isAuthorized()) {
            const whIds: number[] = Api.getWebhookDico();
            const result: IApiEntity[] = Api.getAllEntities().filter((entity: IApiEntity) => {
                const notSystem: boolean = entity.id > Global.TABLE_SYSTEM_MAX_ID;
                const hasAccess: boolean = User.hasTableAccess(entity.id);
                const whEntity: boolean = whIds.includes(entity.id);
                const specialProc: boolean = (entity.specialProc & 2) === 2;
                const notParam: boolean = !this.paramEntity(entity.id);
                return notSystem && hasAccess && !whEntity && !specialProc && notParam;
            });
            return result;
        } else {
            return [];
        }
    }

    public getAllWithIcons(pattern: string = ''): IEntity[] {
        let result: IEntity[] = this.getAll();
        const cards: IModuleCard[] = ModuleCard.getAll();
        cards.forEach((c: IModuleCard) => {
            if (c.type === CardType.List || c.type === CardType.Widget) {
                const card: ICardList | ICardWidget = c.type === CardType.List ? c as ICardList : c as ICardWidget;
                if (card.icon !== Global.DEFAULT_TABLE_ICON) {
                    const list: IList | null = List.get(card.listId);
                    if (list) {
                        const entity: IEntity | null = result.find((e: IEntity) => {
                            return e.id === list.entityId
                        }) || null;
                        if (entity && !entity.icon) {
                            entity.icon = card.icon;
                        }
                    }
                }
            }
        });
        result.forEach((e: IEntity) => {
            if (!e.icon) {
                e.icon = Global.DEFAULT_TABLE_ICON;
            }
        });
        if (pattern) {
            result = filter(result, pattern, ['singular']);
        }
        return sortBy(result, 'singular');
    }

    public get(id: number): IEntity | null {
        const result: IApiEntity | null = Api.getEntity(id);
        if (result) {
            return this.toClient(result);
        }
        return null;
    }

    public async create(singular: string = Msg.get(Message.defaulttablename), fields: Partial<IField>[] = []): Promise<IEntity> {
        if (Auth.isAuthorized()) {
            singular = deepEncode(singular.trim());
            if (singular) {
                const result: IApiEntity = await Api.entityCreate(singular, fields);
                return this.get(result.id) as IEntity;
            } else {
                throw ErrorCode.BadArguments;
            }
        } else {
            throw ErrorCode.AccessDenied;
        }
    }

    public async delete(entityId: number): Promise<number> {
        const entity: IApiEntity | null = Api.getEntity(entityId);
        if (entity) {
            await List.deleteByEntityId(entity.id);
            await ListView.deleteByEntityId(entity.id);
            await ExcelExportTemplate.deleteByEntityId(entity.id);
            const pointers: IApiField[] = this.getPointersTo(entity.id);
            for (let i = 0; i < pointers.length; i++) {
                const related: IField[] = this.getPointerRelatedFields(pointers[i].id);
                for (let i = 0; i < related.length; i++) {
                    const field: IField | null = Field.get(related[i].id);
                    if (field) {
                        await Field.delete(field.id);
                    }
                }
                const pointer: IField | null = Field.get(pointers[i].id);
                if (pointer) {
                    await Field.delete(pointer.id);
                }
            }
            await Api.entityDelete(entity);
            return entityId;
        } else {
            throw ErrorCode.NoEntity;
        }
    }

    public async rename(entityId: number, name: string): Promise<IEntity> {
        const entity: IApiEntity | null = Api.getEntity(entityId);
        name = name.trim();
        if (entity) {
            if (name) {
                entity.singular = name;
                await Api.entitySave(entity);
                return this.get(entityId) as IEntity;
            } else {
                throw ErrorCode.BadArguments;
            }
        } else {
            throw ErrorCode.NoEntity;
        }
    }

    public async toggleRestricted(entityId: number): Promise<boolean> {
        const entity: IApiEntity | null = Api.getEntity(entityId);
        if (entity) {
            if (entity.hasUid) {
                const uidField: IApiField = entity.fields.find((f: IApiField) => {
                    return f.uiType === FieldType.uid;
                }) as IApiField;
                await Api.fieldDelete(uidField);
                return false;
            } else {
                await Api.entityUidCreate(entity);
                return true;
            }
        } else {
            throw ErrorCode.NoEntity;
        }
    }

    public isRestricted(entityId: number): boolean {
        if (User.isRestricted()) {
            const entity: IApiEntity | null = Api.getEntity(entityId);
            if (entity) {
                return !!entity.hasUid;
            }
        }
        return false;
    }

    public getFields(entityId: number): IField[] {
        const entity: IEntity | null = this.get(entityId);
        if (entity) {
            const order: number[] = this.getDefaultFieldsOrder(entity.id);
            return entity.fields.sort((a: IField, b: IField) => {
                return order.indexOf(a.id) - order.indexOf(b.id);
            });
        }
        return [];
    }

    public getFieldsByType(entityId: number, fieldType: FieldType[]): IField[] {
        return this.getFields(entityId).filter((field: IField) => {
            return fieldType.includes(field.uiType);
        });
    }

    /**
    * Returns list of fields which use same pointer as selected linked field.
    * @param {number} entityId - Selected entity id.
    */
    public getRelatedFields(entityId: number): IEntityField[] {
        const result: IEntityField[] = [];
        this.getAll().forEach((entity: IEntity) => {
            entity.fields.forEach((field: IField) => {
                if (field.foreignerEcn === entityId) {
                    result.push({ entity, field });
                }
            });
        });
        return result;
    }

    public hasAccess(entityId: number): boolean {
        return this.getAllRaw().find((e: IApiEntity) => e.id === entityId) !== undefined;
    }

    public getFilteringCandidtes(entityId: number): IEntityFilterCandidate[] {
        const pathFields: IRelationPathField[] = Field.getRelationPathsFields(entityId, EntityFilterTypes, 1);
        return pathFields.filter(((pathField: IRelationPathField) => {
            /* This is to exclude filters for related fields formulas, because they are not calculated */
            return pathField.type !== FieldType.formula || !pathField.path.length;
        })).map((pathField: IRelationPathField) => {
            return {
                id: pathField.id,
                pathId: pathField.pathId,
                name: pathField.pathName,
                field: Field.get(pathField.fieldId) as IField
            } as IEntityFilterCandidate;
        });
    }

    public async setConnection(entityId: number, connection: IConnectionEntity): Promise<IEntity | null> {
        const entity: IApiEntity | null = Api.getEntity(entityId);
        if (entity) {
            entity.UI.connection = connection;
            await Api.entitySave(entity);
            return this.get(entityId);
        }
        return null;
    }

    /* Private Public Methods */

    public getServerFields(entityId: number): IField[] {
        const entity: IEntity | null = this.get(entityId);
        if (entity) {
            return entity.fields.filter((f: IField) => ServerTypes.includes(f.uiType));
        }
        return [];
    }

    public getPrimaryFieldId(entityId: number): number | null {
        const fields: IField[] = this.getPrimaryFieldOptions(entityId);
        return fields.length ? fields[0].id : null;
    }

    public filterFieldsByType(entity: IEntity, types: FieldType[]): IField[] {
        return entity.fields.filter((field: IField) => types.includes(field.uiType));
    }

    public filterFieldsBySystemType(entity: IEntity, type: FieldSystemType): IField[] {
        return entity.fields.filter((field: IField) => field.systemType === type);
    }

    public paramEntity(entityId: number): ParamStorage | null {
        const paramEntities: IApiParamEntity[] = Api.getAllParams();
        const param: IApiParamEntity | null = paramEntities.find((p: IApiParamEntity) => {
            return p.eCN === entityId;
        }) || null;
        return param ? param.name as ParamStorage : null;
    }

    public getDefaultFieldsOrder(entityId: number): number[] {
        const entity: IEntity | null = Entity.get(entityId);
        if (entity) {
            const view: IListView | null = ListView.getEntityGlobalOrNull(entityId);
            const order: number[] = view ? view.state.fieldsOrder : [];
            const result: number[] = order.filter((id: number) => {
                return !!entity.fields.find((f: IField) => f.id === id);
            });
            entity.fields.forEach((f: IField) => {
                if (!result.includes(f.id)) {
                    result.push(f.id);
                }
            });
            return result;
        }
        return [];
    }

    public getField(entity: IEntity, id: number): IField | null {
        return entity.fields.find((f: IField) => f.id === id) || null;
    }

    public allowCache(entityId: number): boolean {
        const entity: IApiEntity | null = Api.getEntity(entityId);
        if (entity) {
            return (entity.specialProc & SpecialProc.DisableCache) !== SpecialProc.DisableCache;
        }
        return false;
    }

    /* Private Methods */

    private toClient(original: IApiEntity): IEntity {
        const fields: IField[] = original.fields.map((field: IApiField) => Field.get(field.id) as IField);
        return {
            id: original.id,
            singular: deepDecode(original.singular),
            fields: fields,
            count: original.nbDoc,
            connection: original.UI && original.UI.connection ? original.UI.connection : null,
            icon: ''
        };
    }

    private getPrimaryFieldOptions(entityId: number): IField[] {
        const entity: IApiEntity | null = Api.getEntity(entityId);
        if (entity) {
            const order: number[] = this.getDefaultFieldsOrder(entity.id);
            return this.getFieldsByType(entityId, PrimaryTypes).sort((a: IField, b: IField) => {
                return order.indexOf(a.id) - order.indexOf(b.id);
            });
        }
        return [];
    }

    private getPointersTo(entityId: number): IApiField[] {
        const result: IApiField[] = [];
        this.getAll().forEach((entity: IEntity) => {
            entity.fields.forEach((field: IField) => {
                if (field.systemType === FieldSystemType.pointer) {
                    const apiField: IApiField = Api.getField(field.id) as IApiField;
                    if (apiField.pointer && apiField.pointer[1].ptEcn === entityId) {
                        result.push(apiField);
                    }
                }
            });
        });
        return result;
    }

    private getPointerRelatedFields(pointerId: number): IField[] {
        const result: IField[] = [];
        this.getAll().forEach((entity: IEntity) => {
            entity.fields.forEach((field: IField) => {
                if (field.systemType === FieldSystemType.linked) {
                    if (field.linkedRelation.includes(pointerId)) {
                        result.push(field);
                    }
                }
            });
        });
        return result;
    }

}

const Entity = new EntitySingleton();

export default Entity;